import { X, Clock, FileText, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ProtocolVersion } from "@/api/protocol-types";

interface ProtocolVersionHistoryProps {
  versions: ProtocolVersion[];
  currentVersionId: string;
  onClose: () => void;
}

export function ProtocolVersionHistory({
  versions,
  currentVersionId,
  onClose,
}: ProtocolVersionHistoryProps) {
  const sorted = [...versions].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">
            Version History
          </h3>
          <span className="text-[10px] text-muted-foreground">
            ({versions.length})
          </span>
        </div>
        <button
          onClick={onClose}
          className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-3 top-0 h-full w-px bg-border" />

          <div className="flex flex-col gap-4">
            {sorted.map((version, i) => {
              const isCurrent = version.id === currentVersionId;
              const isLatest = i === 0;

              return (
                <div key={version.id} className="relative flex gap-3 pl-0">
                  {/* Timeline dot */}
                  <div
                    className={cn(
                      "relative z-10 mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2",
                      isCurrent
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background text-muted-foreground",
                    )}
                  >
                    {isCurrent ? (
                      <Check className="h-3 w-3" />
                    ) : (
                      <FileText className="h-3 w-3" />
                    )}
                  </div>

                  {/* Content */}
                  <div
                    className={cn(
                      "min-w-0 flex-1 rounded-lg border p-3",
                      isCurrent
                        ? "border-primary/30 bg-primary/5"
                        : "border-border bg-card",
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-foreground">
                        v{version.versionNumber}
                      </span>
                      {isCurrent && (
                        <span className="rounded-full bg-primary/10 px-1.5 py-0 text-[9px] font-medium text-primary">
                          Current
                        </span>
                      )}
                      {isLatest && !isCurrent && (
                        <span className="rounded-full bg-muted px-1.5 py-0 text-[9px] font-medium text-muted-foreground">
                          Latest
                        </span>
                      )}
                    </div>

                    {version.changesSummary && (
                      <p className="mt-1 text-[11px] text-muted-foreground">
                        {version.changesSummary}
                      </p>
                    )}

                    <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground">
                      <span>
                        {new Date(version.createdAt).toLocaleDateString(
                          undefined,
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          },
                        )}
                      </span>
                      <span>
                        {new Date(version.createdAt).toLocaleTimeString(
                          undefined,
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
